import React, { useEffect, useRef, useState } from "react";

import * as d3 from "d3";
import { zoom } from "d3-zoom";
import moment from "moment";
import numeral from "numeral";

import { colorPalet } from "@/lib/colorPalet";

const ProjectionChartD3 = ({
  currencySymbol,
  data,
  time,
  width,
  height,
  projectionData,
}) => {
  const chartRef = useRef();
  const chartLineRef = useRef();
  const tooltipRef = useRef();
  const tooltip = d3.select(tooltipRef.current);
  const svg = d3.select(chartRef.current);
  const svgLine = d3.select(chartLineRef.current);
  const margin = { top: 20, right: 20, bottom: 30, left: 50 };

  // const innerWidth = width - margin.left - margin.right;
  // const innerHeight = height - margin.top - margin.bottom;

  const [dimensions, setDimensions] = useState({ width, height });

  useEffect(() => {
    const handleResize = () => {
      const newWidth = chartRef.current.parentElement.offsetWidth;
      const newHeight = height;
      setDimensions({ width: newWidth, height: newHeight });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [height]);

  useEffect(() => {
    if (data && chartRef.current) {
      // Accessors
      const xAccessor = (d) => moment(d?.x);
      const yAccessor = (d) => parseFloat(d?.y);

      // Sort Data
      data?.sort((a, b) => d3.ascending(xAccessor(a), xAccessor(b)));

      let xScale = d3
        .scaleTime()
        .domain(
          d3.extent(
            [
              ...data,
              ...(projectionData?.[0] || []),
              ...(projectionData?.[1] || []),
              ...(projectionData?.[2] || []),
              ...(projectionData?.[3] || []),
            ],
            xAccessor,
          ),
        )
        .range([0, dimensions.width - margin.left - margin.right]);
      const newX = xScale;
      const yScale = d3
        .scaleLinear()
        .domain(
          d3.extent(
            [
              ...data,
              ...(projectionData?.[0] || []),
              ...(projectionData?.[1] || []),
              ...(projectionData?.[2] || []),
              ...(projectionData?.[3] || []),
            ],
            yAccessor,
          ),
        )
        .range([dimensions.height - margin.top - margin.bottom, 0])
        .nice();

      const line = d3
        .line()
        .x((d) => xScale(xAccessor(d)))
        .y((d) => yScale(yAccessor(d)));

      const area = d3
        .area()
        .x((d) => xScale(xAccessor(d)))
        .y0(dimensions.height - margin.top - margin.bottom)
        .y1((d) => yScale(yAccessor(d)));

      const xAxis = d3
        .axisBottom(xScale)
        .ticks(
          window.outerWidth <= 768
            ? 5
            : time === "1Y"
              ? 12
              : time === "5Y"
                ? 10
                : 20,
        )
        .tickFormat((d) => moment(d).format(time === "1Y" ? "MMM YY" : "YYYY"));

      const yAxis = d3
        .axisRight(yScale)
        .tickFormat(
          (d) =>
            `${currencySymbol ? currencySymbol : ""}${numeral(d)
              .format("0.0a")
              .toUpperCase()}`,
        );

      // .tickFormat((d) => `${currencySymbol ? currencySymbol : ""}${d}`);

      const dateBisector = d3.bisector(xAccessor).left;

      const handleZoom = (event) => {
        const { transform } = event;

        xScale = transform.rescaleX(newX);

        // Calculate the new x-domain to set the data range
        const xDomain = xScale.domain();
        const dataDomain = newX.domain();
        const [minData, maxData] = dataDomain;
        let [minX, maxX] = xDomain;
        minX = Math.max(minData, minX);
        maxX = Math.min(maxData, maxX);
        xScale.domain([minX, maxX]);

        // Update x-axis
        svg.select(".x-axis-line").call(xAxis.scale(xScale));

        // Update area path
        svg.select(".area-path").attr(
          "d",
          area.x((d) => xScale(d.x)),
        );

        // Update line path
        svg.select(".line-path").attr(
          "d",
          line.x((d) => xScale(d.x)),
        );
        svg.select(".line-path-1").attr(
          "d",
          line.x((d) => xScale(d.x)),
        );
        svg.select(".line-path-2").attr(
          "d",
          line.x((d) => xScale(d.x)),
        );
        svg.select(".line-path-3").attr(
          "d",
          line.x((d) => xScale(d.x)),
        );
        svg.select(".line-path-4").attr(
          "d",
          line.x((d) => xScale(d.x)),
        );

        // Apply cursor grabbing style when dragging
        if (event.sourceEvent && event.sourceEvent?.type === "mousemove") {
          svg.style("cursor", "grabbing");
        } else {
          svg.style("cursor", null);
        }
      };

      const zoomHandler = zoom()
        .scaleExtent([1, 4])
        .translateExtent([
          [0, 0],
          [width, height],
        ])
        .on("zoom", handleZoom);

      d3.select(chartRef.current).selectAll("*").remove();
      d3.select(chartLineRef.current).selectAll("*").remove();
      const tooltipDot = svg
        .append("g")
        .attr("transform", `translate(0, ${margin.top})`)
        .append("circle")
        .attr("r", 5)
        .attr("fill", colorPalet.tykrFairValue)
        .attr("stroke", colorPalet.hover)
        .attr("stroke-width", 7)
        .style("opacity", 0)
        .style("pointer-events", "none")
        .attr("class", "hover-point");

      svg
        .append("defs")
        .append("linearGradient")
        .attr("id", "areaGradient")
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", "0%")
        .attr("x2", "0%")
        .attr("y1", "0%")
        .attr("y2", "100%")
        .append("stop")
        .attr("offset", "0%")
        .style("stop-color", "rgba(0, 59, 179, 0.15)")
        .append("stop")
        .attr("offset", "100%")
        .style("stop-color", "rgba(0, 59, 179, 0)");

      svg
        .append("g")
        .attr("transform", `translate(0, ${margin.top})`)
        .append("path")
        .datum(data)
        .attr("fill", "url(#areaGradient)")
        .attr("d", area)
        .attr("class", "area-path");

      svg
        .append("g")
        .attr("transform", `translate(0, ${margin.top})`)
        .append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", colorPalet.tykrFairValue)
        .attr("stroke-width", 2)
        .attr("d", line)
        .attr("class", "line-path");

      projectionData?.forEach((forecastData, index) => {
        svg
          .append("path")
          .datum(forecastData)
          .attr("transform", `translate(0, ${margin.top})`)
          .attr("fill", "none")
          .attr(
            "stroke",
            [
              colorPalet.tykrFairValue,
              colorPalet.rule1FairValue,
              colorPalet.grahamFairValue,
              colorPalet.textMuted,
            ][index] || colorPalet.primary,
          )
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "3,8")
          .attr("d", line)
          .attr("class", `line-path-${index + 1}`);
      });

      svg.call(zoomHandler);

      svgLine
        .append("g")
        .attr("transform", `translate(${0}, ${margin.top})`)
        .call(yAxis)
        .attr("class", "y-axis-line")
        .style("font-size", "14px")
        .style("color", colorPalet.chartLine);

      svg
        .append("g")
        .attr("transform", `translate(0, ${dimensions.height - margin.bottom})`)
        .call(xAxis)
        .attr("class", "x-axis-line")
        .style("font-size", "14px")
        .style("color", colorPalet.chartLine);

      svg
        .append("g")
        .attr("transform", `translate(0, ${margin.top})`)
        .append("rect")
        .attr("class", "hover-rect")
        .attr("width", dimensions.width - margin.left - margin.right)
        .attr("height", dimensions.height - margin.top - margin.bottom)
        .style("opacity", 0)
        .style("cursor", "pointer")
        .on("touchmouse mousemove", (event) => {
          const mousePos = d3.pointer(event, svg.node());
          const date = xScale.invert(mousePos[0]);
          const allData = [
            ...data,
            ...(projectionData?.[0] || []),
            ...(projectionData?.[1] || []),
            ...(projectionData?.[2] || []),
            ...(projectionData?.[3] || []),
          ];
          const bisectionIndex = dateBisector(allData, date, 1);
          const hoveredIndexData = allData[bisectionIndex - 1];
          const isLastPoint = bisectionIndex === allData.length;
          if (!isLastPoint) {
            tooltipDot
              .style("opacity", 1)
              .attr("cx", xScale(xAccessor(hoveredIndexData)))
              .attr("cy", yScale(yAccessor(hoveredIndexData)))
              .raise();

            tooltip
              .style("display", "block")
              .style("top", `${yScale(yAccessor(hoveredIndexData)) + 40}px`)
              .style("left", `${xScale(xAccessor(hoveredIndexData))}px`);

            const tooltipContent = `<ul style="list-style: none; padding: 0; margin: 0;">
                  <li style="background-color:${colorPalet.tooltipBackground};font-size: 12px; padding:7px 60px 7px 7px;">
                         <b>${moment(xAccessor(hoveredIndexData)).format("YYYY-MM-DD")}</b>
                 </li>
                 <li style="background-color:${colorPalet.secondary};font-size: 12px; padding:10px 60px 10px 10px;">
                 <div>
                 <div style="max-width:10px; width:100%; min-height:10px; height:100%; background-color:${colorPalet.primary};border-radius: 50%"></div>
                     <div style="padding-left:15px; margin-top:-13px;">${`Price: <b>${currencySymbol}${yAccessor(hoveredIndexData)?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b>`}
                    </div>
                 </div>
                 </li>
                 </ul>`;
            tooltip.html(tooltipContent);
          } else {
            tooltipDot.style("opacity", 0);
            tooltip.style("display", "none");
          }
        })
        .on("mouseleave", () => {
          tooltipDot.style("opacity", 0);
          tooltip.style("display", "none");
        });
    }
  }, [data, time, dimensions, currencySymbol, projectionData]);

  return (
    <>
      <div className="relative">
        <svg
          ref={chartLineRef}
          width={80}
          height={dimensions.height || 0}
          className="projection-chart-line"
        ></svg>
        <svg
          ref={chartRef}
          width={dimensions.width ? dimensions.width - 52 : 0}
          height={dimensions.height || 0}
          className="projection-chart-line-path"
        ></svg>
      </div>
      <div ref={tooltipRef} className={`tooltip d-none`}></div>
    </>
  );
};

export default ProjectionChartD3;
